import { useEffect, useMemo, useState } from 'react'
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import IconButton from '../../components/IconButton'
import { useHint } from '../../components/useHint'
import PageHeader from '../../components/PageHeader'
import { useToast } from '../../components/toastContext'
import CertificateEditor from './CertificateEditor'
import CertificateFormDrawer from './CertificateFormDrawer'
import {
  cloneCertificate,
  deleteCertificate,
  listCertificates,
  saveCertificate,
  saveTemplateLayout,
} from './contentService'
import { certificatesStrings as S } from './strings'
import './content.css'
import { serverText } from '../../lib/i18n'

/**
 * إدارة الشهادات — Views/CertificateSettings/Index.cshtml.
 *
 * The two template buttons open the canvas editor for the Arabic and English
 * artwork; the legacy renders them as literal `ع` and `EN` chips, which is kept.
 */
export default function CertificatesPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one: without this the table renders its "no records"
     state for a database fault, which tells the operator the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const toast = useToast()
  const [pendingDelete, setPendingDelete] = useState(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  /* `null` when closed; otherwise the row plus which artwork is being laid out. */
  const [designing, setDesigning] = useState(null)
  const [pendingClone, setPendingClone] = useState(null)

  const openForm = (certificate) => {
    setEditing(certificate ?? null)
    setFormOpen(true)
  }

  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    listCertificates()
      .then((response) => {
        setLoadError(null)
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        setLoadError(error)
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.actionFailed)
      })
  }, [reloadKey, toast])

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="content__index">{index + 1}</span>,
      },
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'nameEn',
        header: S.columns.nameEn,
        render: (row) => <bdi dir="ltr">{row.nameEn}</bdi>,
      },
      {
        key: 'uploadedOn',
        header: S.columns.uploadedOn,
        render: (row) => (
          <span className="content__date" dir="ltr">
            {row.uploadedOn ?? ''}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '210px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="data-table__row-actions">
            <LangButton
              className="content__lang"
              label={S.row.arabicTemplate}
              /* THE TEMPLATE TYPE, not its caption. These passed `S.row.arabicTemplate` —
                 the translated label — as the language, so `language === 'english'` was
                 never true: the EN button opened the ARABIC artwork and the ARABIC layout,
                 and saving posted "نموذج اللغة الإنجليزية" as the templateType.

                 Neither button is gated on the artwork existing: the server hands out
                 `root/CTemplate/{PathMap}` for every certificate whether or not a PNG was
                 ever written there, so a row cannot tell from the list. The editor probes
                 the image itself and says so when it is missing. */
              onClick={() => setDesigning({ row, language: 'arabic' })}
            >
              ع
            </LangButton>
            <LangButton
              className="content__lang content__lang--en"
              label={S.row.englishTemplate}
              onClick={() => setDesigning({ row, language: 'english' })}
            >
              EN
            </LangButton>
            <IconButton icon={Pencil} label={S.row.edit} onClick={() => openForm(row)} />
            {/* Cloning writes a new template, so it asks first — but NOT in the shape of
                the delete beside it; see the dialog below. It had no handler at all. */}
            <IconButton
              icon={Copy}
              label={S.row.clone}
              tone="primary"
              onClick={() => setPendingClone(row)}
            />
            <IconButton
              icon={Trash2}
              label={S.row.delete}
              tone="danger"
              onClick={() => setPendingDelete(row)}
            />
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={loading ? undefined : rows.length}
        actions={
          <Button onClick={() => openForm(null)}>
            <Plus size={16} strokeWidth={2} />
            {S.actions.create}
          </Button>
        }
      />

      <DataTable
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row) => row.id}
        initialSort={{ key: 'name', dir: 'asc' }}
      />

      <CertificateFormDrawer
        open={formOpen}
        certificate={editing}
        onClose={() => setFormOpen(false)}
        onSave={async (values) => {
          const outcome = await saveCertificate({ ...values, id: editing?.id })
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setFormOpen(false)
          toast.success(serverText(outcome.message) || S.saved)
          setReloadKey((key) => key + 1)
        }}
      />

      <CertificateEditor
        open={!!designing}
        certificate={designing?.row}
        language={designing?.language}
        onClose={() => setDesigning(null)}
        /* The layout is per LANGUAGE — the same certificate has two, and the editor is
           opened for one of them. */
        onSave={async (layout) => {
          const outcome = await saveTemplateLayout({
            certificateId: designing.row.id,
            templateType: designing.language,
            layout,
          })
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setDesigning(null)
          toast.success(serverText(outcome.message) || S.editor.saved)
        }}
      />

      {/*
        A COPY IS NOT A DELETE, and this dialog has to say all three of those things itself.
        `ConfirmDialog` defaults to the destructive shape it was built for - `tone="danger"`,
        `title={kit.confirmDelete}` and `confirmLabel={kit.delete}` - so passing only a
        message opened the copy under a red warning triangle headed "Confirm delete", with
        a red "Delete" button, over a sentence asking about a copy. `primary` is the utility
        tone the row's own Copy button already carries.
      */}
      <ConfirmDialog
        open={!!pendingClone}
        tone="primary"
        title={S.cloneTitle}
        message={S.cloneConfirm}
        confirmLabel={S.row.clone}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await cloneCertificate(pendingClone.id)
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setPendingClone(null)
          toast.success(serverText(outcome.message) || S.saved)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingClone(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        cancelLabel={S.close}
        onConfirm={async () => {
          const outcome = await deleteCertificate(pendingDelete.id)
          if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}

/**
 * `ع` / `EN` — the two artwork buttons.
 *
 * A single letter is not a label, which is why they carried a `title` saying which template
 * it opens. That was the browser's bubble, on a row whose other three controls now show the
 * app's; and with only the letter as an accessible name, a screen reader read the Arabic
 * one as the letter ain. Both are fixed here.
 */
function LangButton({ className, label, children, ...rest }) {
  const { hint, bubble } = useHint(label)

  return (
    <>
      <button type="button" className={className} aria-label={label} {...hint} {...rest}>
        {children}
      </button>
      {bubble}
    </>
  )
}
